import { css } from '@emotion/react'
import { Theme } from '@mui/material'

export const builderContainerStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

export const builderHeaderStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(2)} ${theme.spacing(3)};
  background-color: ${theme.palette.background.paper};
  border-bottom: 1px solid ${theme.palette.divider};
  flex-shrink: 0;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: ${theme.palette.text.primary};
  }
`

export const builderContentStyles = css`
  display: flex;
  flex: 1;
  overflow: hidden;
`

export const builderSidebarStyles = (theme: Theme) => css`
  width: 450px;
  min-width: 450px;
  background-color: ${theme.palette.background.paper};
  border-right: 1px solid ${theme.palette.divider};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const builderPreviewStyles = css`
  flex: 1;
  overflow: hidden;
  position: relative;
`

export const tabPanelStyles = css`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`

export const formSectionStyles = (theme: Theme) => css`
  padding: ${theme.spacing(3)};
  border-bottom: 1px solid ${theme.palette.divider};

  &:last-child {
    border-bottom: none;
  }
`

export const formLabelStyles = (theme: Theme) => css`
  font-weight: 600;
  margin-bottom: ${theme.spacing(1)};
  color: ${theme.palette.text.primary};
  font-size: 0.875rem;
`

export const formHelperTextStyles = (theme: Theme) => css`
  font-size: 0.75rem;
  color: ${theme.palette.text.secondary};
  font-style: italic;
  margin-top: ${theme.spacing(0.5)};
`

export const chapterListStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`

export const chapterItemStyles = (theme: Theme, isSelected: boolean) => css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  background-color: ${isSelected ? theme.palette.primary.main + '10' : theme.palette.grey[50]};
  border: 1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider};
  border-radius: ${theme.shape.borderRadius}px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${isSelected ? theme.palette.primary.main + '15' : theme.palette.grey[100]};
  }

  .chapter-title {
    flex: 1;
    font-weight: 500;
    color: ${theme.palette.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chapter-actions {
    display: flex;
    gap: ${theme.spacing(0.5)};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .chapter-actions {
    opacity: 1;
  }
`

export const stepListStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
  margin-left: ${theme.spacing(3)};
  margin-top: ${theme.spacing(1)};
`

export const stepItemStyles = (theme: Theme, isSelected: boolean) => css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1)} ${theme.spacing(1.5)};
  background-color: ${isSelected ? theme.palette.secondary.main + '10' : 'transparent'};
  border: 1px solid ${isSelected ? theme.palette.secondary.main : 'transparent'};
  border-radius: ${theme.shape.borderRadius}px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.palette.grey[100]};
  }

  .step-title {
    flex: 1;
    font-size: 0.875rem;
    color: ${theme.palette.text.secondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .step-actions {
    display: flex;
    gap: ${theme.spacing(0.5)};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .step-actions {
    opacity: 1;
  }
`

export const sectionEditorStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(2)};
  background-color: ${theme.palette.grey[50]};
  border-radius: ${theme.shape.borderRadius}px;
  margin-bottom: ${theme.spacing(1)};
`

export const colorPickerStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};

  .color-swatch {
    width: 36px;
    height: 36px;
    border-radius: ${theme.shape.borderRadius}px;
    border: 1px solid ${theme.palette.divider};
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  .color-input {
    flex: 1;
  }
`

export const mapCaptureButtonStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  background-color: ${theme.palette.primary.main};
  color: ${theme.palette.primary.contrastText};
  border: none;
  border-radius: ${theme.shape.borderRadius}px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.palette.primary.dark};
  }

  &:disabled {
    background-color: ${theme.palette.grey[300]};
    cursor: not-allowed;
  }
`

export const mediaGridStyles = (theme: Theme) => css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing(1)};
`

export const mediaItemStyles = (theme: Theme) => css`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.shape.borderRadius}px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${theme.palette.primary.main};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .media-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .media-overlay {
    opacity: 1;
  }
`

export const emptyStateStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing(4)};
  text-align: center;
  color: ${theme.palette.text.secondary};

  svg {
    font-size: 48px;
    margin-bottom: ${theme.spacing(2)};
    opacity: 0.5;
  }

  h3 {
    margin: 0 0 ${theme.spacing(1)};
    font-weight: 500;
    color: ${theme.palette.text.primary};
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`

export const accordionStyles = (theme: Theme) => css`
  &.MuiAccordion-root {
    background-color: transparent;
    box-shadow: none;
    border: 1px solid ${theme.palette.divider};
    border-radius: ${theme.shape.borderRadius}px !important;
    margin-bottom: ${theme.spacing(1)};

    &:before {
      display: none;
    }

    &.Mui-expanded {
      margin-bottom: ${theme.spacing(1)};
    }
  }

  .MuiAccordionSummary-root {
    min-height: 48px;
    padding: 0 ${theme.spacing(2)};

    &.Mui-expanded {
      min-height: 48px;
    }
  }

  .MuiAccordionSummary-content {
    margin: ${theme.spacing(1.5)} 0;

    &.Mui-expanded {
      margin: ${theme.spacing(1.5)} 0;
    }
  }

  .MuiAccordionDetails-root {
    padding: ${theme.spacing(2)};
    border-top: 1px solid ${theme.palette.divider};
  }
`

export const previewPlaceholderStyles = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: ${theme.palette.grey[100]};
  color: ${theme.palette.text.secondary};

  svg {
    font-size: 64px;
    margin-bottom: ${theme.spacing(2)};
    opacity: 0.3;
  }

  h2 {
    margin: 0 0 ${theme.spacing(1)};
    font-weight: 500;
    color: ${theme.palette.text.primary};
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    max-width: 300px;
    text-align: center;
  }
`

export const navbarStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(1)} ${theme.spacing(2)};
  background-color: ${theme.palette.grey[50]};
  border-bottom: 1px solid ${theme.palette.divider};
`

export const breadcrumbStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  color: ${theme.palette.text.secondary};
  font-size: 0.875rem;

  a {
    color: ${theme.palette.primary.main};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`
